import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Color, Card, Text } from '@bb/elements-web';
import { AuthContext } from 'contexts';
import { sendPaymentUpdatedNotification } from 'api/NotificationController';
import BillingCard from './components/BillingCard';
import ManageCard from '../../../../components/ManageCard';
import ManageCardContextProvider from '../../../../components/ManageCard/ManageCardContext';
import { ViewingLevel } from '../..';
import {
  CanManageBillingProfileType,
  OverdueBalanceType,
} from '../../SubscriptionsPageContext';

const defaultCanManageBilling: CanManageBillingProfileType = {
  response: true,
};

export type PaymentCardProps = {
  /** Can the current user manage billing details */
  canManageBilling: CanManageBillingProfileType;
  /** Whether these are the subscriptions for an individual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** The ID of the user viewing this page */
  userId?: string;
};

/**
 * Displays the Payment card on the Subscriptions & Payments page.
 * This card can be in a few different states, including:
 * - Payment in good standing: displays the user's next invoice date
 * - Payment past due: displays the user's outstanding balance and next invoice date
 * - No active subscriptions: displays a message indicating no paid subscriptions
 */
const PaymentCard: React.FC<PaymentCardProps> = ({
  canManageBilling = defaultCanManageBilling,
  viewingLevel,
  userId,
}) => {
  const { auth } = useContext(AuthContext);

  // Returns response true if the user has an overdue balance
  const isSubscriptionOverdue = useMemo<OverdueBalanceType>(() => {
    if (auth.isInBillingCollection || !auth.supraPaid) {
      return {
        response: true,
        message: 'TODO: Suspended & deactivated pricing explanation here',
      };
    }

    return {
      response: false,
    };
  }, [auth.isInBillingCollection, auth.supraPaid]);

  // TODO: Remove this variable in KB-1686, only being set to accomodate legacy ManageCard component.
  const productType = userId ? 'appointmentDesk' : 'orgBilling';

  return (
    <ManageCardContextProvider
      orgId={auth.org.id}
      billingCustomerNumber={auth.billingCustomerNumber}
      canManageBilling={canManageBilling}
      isSubscriptionOverdue={isSubscriptionOverdue}
    >
      <StyledCard title="Payment" dataCy="payment-card">
        <Subtitle>
          All subscriptions are billed in the same billing cycle
        </Subtitle>
        <StyledBody>
          <StyledSection>
            <StyledHeader color="black" thickness="semiBold" size="large">
              {isSubscriptionOverdue.response
                ? 'Outstanding balance'
                : 'Billing'}
            </StyledHeader>
            <BillingCard
              isSubscriptionOverdue={isSubscriptionOverdue}
              orgId={auth.org.id}
              billingCustomerNumber={auth.billingCustomerNumber}
              viewingLevel={viewingLevel}
            />
          </StyledSection>
          <StyledSection>
            <StyledHeader color="black" thickness="semiBold" size="large">
              Payment method
            </StyledHeader>
            {/* TODO: This is a temporary solution using the legacy (shared) ManageCard component.
            Future plans will be to create a NEW ManageCard component without Stripe-related logic.
            Work to be done in KB-1686. */}
            <ManageCard
              userId={userId}
              productType={productType}
              stripePk={process.env.STRIPE_PK!}
              canManageBilling={canManageBilling}
              isSubscriptionOverdue={isSubscriptionOverdue}
              onCardUpdateSuccess={() => {
                if (viewingLevel === 'org' && auth?.role !== 'OWNER')
                  sendPaymentUpdatedNotification();
              }}
            />
          </StyledSection>
        </StyledBody>
      </StyledCard>
    </ManageCardContextProvider>
  );
};

export default PaymentCard;

// Styled components
const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-size: 20px;
    font-weight: 500;
  }
  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
    min-height: auto;
  }
  .ant-card-body {
    padding-top: 8px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #585e72;
  margin: -12px 0 16px 0;
`;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
  border-radius: 8px;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid ${Color.LIGHTER_GREY};
  border-radius: 4px;
  padding: 16px;
`;

const StyledHeader = styled(Text)`
  margin-bottom: 8px;
`;
